import * as pdfMake from "pdfmake/build/pdfmake";
import * as pdfFonts from "pdfmake/build/vfs_fonts";
import dayjs from "dayjs";
import { fetchImageAsBase64 } from "../../../../../../../utils/imageUtils";

//APP
import { imgLogo } from "../../../../../../../utils/base64-Imgs";
import { typeEstruturaCompraInsumos, typeInsumos } from "./index";

(pdfMake as any).vfs = pdfFonts.pdfMake.vfs;

export interface HeaderDef {
    logo: string;
    titulo: string;
    nome_arquivo?: string;
    razaoSocial: string;
    cliente?: string;
    safra?: string;
    cultura?: string;
    autor?: string;
}

const buildComposicaoChips = (composicao: any[], composicao_bioinsumo: any[], zebraColor: string | null): any => {
    const hasComposicao = Array.isArray(composicao) && composicao.length > 0;
    const hasBioinsumo = Array.isArray(composicao_bioinsumo) && composicao_bioinsumo.length > 0;

    if (!hasComposicao && !hasBioinsumo) {
        return { text: '-', margin: [2, 4, 2, 4], fillColor: zebraColor };
    }

    const isBioinsumo = !hasComposicao && hasBioinsumo;
    const color = isBioinsumo ? '#51c11a' : '#1a73e8';
    const bgColor = isBioinsumo ? '#effedf' : '#e8f0fe';

    const chips = hasComposicao
        ? composicao.filter((c: any) => c?.insumo_nome).map((c: any) => {
            const label = `${c.insumo_nome}${c?.concentracao != null ? ` | C: ${c.concentracao}%` : ''}`;
            return { _label: label };
        })
        : composicao_bioinsumo.filter((c: any) => c?.microrganismo_nome).map((c: any) => {
            const label = `${c.microrganismo_nome}${c?.concentracao != null ? ` | C: ${c.concentracao}` : ''}`;
            return { _label: label };
        });

    if (chips.length === 0) {
        return { text: '-', margin: [2, 4, 2, 4], fillColor: zebraColor };
    }

    const chipNodes = chips.map(({ _label }: any) => ({
        width: 'auto',
        table: {
            widths: ['auto'],
            body: [[{
                text: _label,
                fontSize: 7.5,
                bold: true,
                color: color,
                fillColor: bgColor,
                margin: [3, 1, 3, 1],
                border: [true, true, true, true]
            }]]
        },
        layout: {
            hLineWidth: () => 1,
            vLineWidth: () => 1,
            hLineColor: () => color,
            vLineColor: () => color
        }
    }));

    const rows: any[][] = [];
    for (let i = 0; i < chipNodes.length; i += 3) {
        rows.push(chipNodes.slice(i, i + 3));
    }

    return {
        stack: rows.map(row => ({
            columns: row,
            columnGap: 3,
            margin: [0, 1, 0, 1],
        })),
        margin: [2, 3, 2, 3],
        fillColor: zebraColor
    };
};

export interface OpcoesColunas {
    exibir_tipo?: boolean;
    exibir_classe?: boolean;
    exibir_composicao?: boolean;
}

export const gerarDocDefinition = (
    dados: (typeEstruturaCompraInsumos | typeInsumos)[],
    headerInfo?: HeaderDef,
    opcoes?: OpcoesColunas
) => {
    const mostrarTipo = opcoes?.exibir_tipo !== false;
    const mostrarClasse = opcoes?.exibir_classe !== false;
    const mostrarComposicao = opcoes?.exibir_composicao !== false;
    const content: any[] = [];

    // Header Principal (Somente Primeira Página)
    content.push({
        stack: [
            {
                image: headerInfo?.logo || 'imgLogoAlias',
                width: 80,
                alignment: 'center',
                margin: [0, 0, 0, 10]
            },
            {
                text: `${headerInfo?.titulo}`,
                style: 'headerMain',
                alignment: 'center',
                margin: [0, 0, 0, 10]
            },
            {
                canvas: [
                    {
                        type: 'rect',
                        x: 0,
                        y: 0,
                        w: 760,
                        h: 4,
                        linearGradient: ['#e5f1e1', '#5da57d', '#0e743f']
                    }
                ],
            },
            {
                stack: [
                    {
                        text: `${headerInfo?.razaoSocial}`,
                        fontSize: 8,
                        bold: true,
                        color: '#4D4D4D',
                        alignment: 'center'
                    },
                    ...(headerInfo?.cliente ? [{
                        margin: [0, 10, 0, 0],
                        table: {
                            widths: ['*', '30%', '30%'],
                            body: [
                                [
                                    {
                                        text: [
                                            { text: 'CLIENTE\n', fontSize: 8, color: '#7b7b7b', bold: true },
                                            { text: (headerInfo.cliente || '-').toUpperCase(), fontSize: 11, bold: true, color: '#000000' }
                                        ],
                                        alignment: 'center',
                                        margin: [5, 5, 5, 5]
                                    },
                                    {
                                        text: [
                                            { text: 'SAFRA\n', fontSize: 8, color: '#7b7b7b', bold: true },
                                            { text: (headerInfo.safra || '-').toUpperCase(), fontSize: 11, bold: true, color: '#000000' }
                                        ],
                                        alignment: 'center',
                                        margin: [5, 5, 5, 5]
                                    },
                                    {
                                        text: [
                                            { text: 'CULTURA\n', fontSize: 8, color: '#7b7b7b', bold: true },
                                            { text: (headerInfo.cultura || '-').toUpperCase(), fontSize: 11, bold: true, color: '#000000' }
                                        ],
                                        alignment: 'center',
                                        margin: [5, 5, 5, 5]
                                    }
                                ]
                            ]
                        },
                        layout: {
                            hLineWidth: () => 1,
                            vLineWidth: () => 1,
                            hLineColor: () => '#7b7b7b',
                            vLineColor: () => '#7b7b7b'
                        }
                    }] : [])
                ],
                margin: [0, 5, 0, 20]
            }
        ],
        margin: [0, 0, 0, 10]
    });

    const isJuntarInsumos = dados.length > 0 && !('insumos' in dados[0]);

    if (isJuntarInsumos) {
        let globalInsumoIndex = 0; // Para controle de zebra
        const corHeader = '#0e743f';

        const headerRow: any[] = [
            { text: 'Insumo', style: 'tableHeader', alignment: 'left', fillColor: corHeader, color: 'white' },
            ...(mostrarTipo ? [{ text: 'Tipo', style: 'tableHeader', alignment: 'left', fillColor: corHeader, color: 'white' }] : []),
            ...(mostrarClasse ? [{ text: 'Classe Agronômica', style: 'tableHeader', alignment: 'left', fillColor: corHeader, color: 'white' }] : []),
            ...(mostrarComposicao ? [{ text: 'Composição/Microrganismo', style: 'tableHeader', alignment: 'left', fillColor: corHeader, color: 'white' }] : []),
            { text: 'Quantidade', style: 'tableHeader', alignment: 'left', fillColor: corHeader, color: 'white' },
        ];
        const colCount = headerRow.length;
        const bodyInsumos: any[] = [headerRow];

        (dados as typeInsumos[]).forEach((insu) => {
            const zebraColor = (globalInsumoIndex % 2 === 0) ? null : '#e5f1e1';

            bodyInsumos.push([
                { text: insu.insumo_nome || '-', margin: [2, 4, 2, 4], alignment: 'left', fillColor: zebraColor },
                ...(mostrarTipo ? [{ text: insu.nome_tipo || '-', margin: [2, 4, 2, 4], alignment: 'left', fillColor: zebraColor }] : []),
                ...(mostrarClasse ? [{ text: insu.classe_agronomica_nome || '-', margin: [2, 4, 2, 4], alignment: 'left', fillColor: zebraColor }] : []),
                ...(mostrarComposicao ? [buildComposicaoChips(insu.composicao, insu.composicao_bioinsumo, zebraColor)] : []),
                { text: insu.quantidade_total_view || '-', margin: [2, 4, 2, 4], alignment: 'left', fillColor: zebraColor },
            ]);

            globalInsumoIndex++;
        });

        if (dados.length === 0) {
            const emptyRow: any[] = [
                { text: 'Nenhum insumo planejado.', colSpan: colCount, margin: [2, 4, 2, 4], alignment: 'center', italics: true, fillColor: null },
                ...Array(colCount - 1).fill({}),
            ];
            bodyInsumos.push(emptyRow);
        }

        const colWeights = [
            { peso: 25, ativo: true },
            { peso: 12, ativo: mostrarTipo },
            { peso: 15, ativo: mostrarClasse },
            { peso: 33, ativo: mostrarComposicao },
            { peso: 15, ativo: true },
        ];
        const totalPeso = colWeights.filter(c => c.ativo).reduce((acc, c) => acc + c.peso, 0);
        const widths: string[] = colWeights
            .filter(c => c.ativo)
            .map(c => `${((c.peso / totalPeso) * 100).toFixed(2)}%`);

        content.push({
            table: {
                widths,
                body: bodyInsumos
            },
            layout: {
                fillColor: function (rowIndex: number, node: any, columnIndex: number) {
                    if (rowIndex === 0) return '#a6a6a6'; // Header principal
                    return null;
                }
            },
            margin: [0, 0, 0, 15]
        });
    } else {
        (dados as typeEstruturaCompraInsumos[]).forEach((propriedade) => {
            // Header Propriedade
            content.push({
                table: {
                    widths: ['*'],
                    body: [
                        [
                            {
                                text: propriedade.nome_propriedade || "PROPRIEDADE NÃO INFORMADA",
                                style: 'headerPropriedade',
                                alignment: 'center',
                                fillColor: '#5da57d',
                                color: 'white',
                                bold: true,
                                margin: [0, 5, 0, 5]
                            }
                        ]
                    ]
                },
                layout: 'noBorders',
                margin: [0, 0, 0, 10]
            });

            let globalInsumoIndex = 0; // Para controle de zebra
            const corHeader = '#0e743f';

            const headerRowProp: any[] = [
                { text: 'Insumo', style: 'tableHeader', alignment: 'left', fillColor: corHeader, color: 'white' },
                ...(mostrarTipo ? [{ text: 'Tipo', style: 'tableHeader', alignment: 'left', fillColor: corHeader, color: 'white' }] : []),
                ...(mostrarClasse ? [{ text: 'Classe Agronômica', style: 'tableHeader', alignment: 'left', fillColor: corHeader, color: 'white' }] : []),
                ...(mostrarComposicao ? [{ text: 'Composição/Microrganismo', style: 'tableHeader', alignment: 'left', fillColor: corHeader, color: 'white' }] : []),
                { text: 'Quantidade', style: 'tableHeader', alignment: 'left', fillColor: corHeader, color: 'white' },
            ];
            const colCountProp = headerRowProp.length;
            const bodyInsumos: any[] = [headerRowProp];

            propriedade.insumos.forEach((insu) => {
                const zebraColor = (globalInsumoIndex % 2 === 0) ? null : '#e5f1e1';

                bodyInsumos.push([
                    { text: insu.insumo_nome || '-', margin: [2, 4, 2, 4], alignment: 'left', fillColor: zebraColor },
                    ...(mostrarTipo ? [{ text: insu.nome_tipo || '-', margin: [2, 4, 2, 4], alignment: 'left', fillColor: zebraColor }] : []),
                    ...(mostrarClasse ? [{ text: insu.classe_agronomica_nome || '-', margin: [2, 4, 2, 4], alignment: 'left', fillColor: zebraColor }] : []),
                    ...(mostrarComposicao ? [buildComposicaoChips(insu.composicao, insu.composicao_bioinsumo, zebraColor)] : []),
                    { text: insu.quantidade_total_view || '-', margin: [2, 4, 2, 4], alignment: 'left', fillColor: zebraColor },
                ]);

                globalInsumoIndex++;
            });

            if (propriedade.insumos.length === 0) {
                const emptyRowProp: any[] = [
                    { text: 'Nenhum insumo planejado para esta propriedade.', colSpan: colCountProp, margin: [2, 4, 2, 4], alignment: 'center', italics: true, fillColor: null },
                    ...Array(colCountProp - 1).fill({}),
                ];
                bodyInsumos.push(emptyRowProp);
            }

            const colWeightsProp = [
                { peso: 25, ativo: true },
                { peso: 12, ativo: mostrarTipo },
                { peso: 15, ativo: mostrarClasse },
                { peso: 33, ativo: mostrarComposicao },
                { peso: 15, ativo: true },
            ];
            const totalPesoProp = colWeightsProp.filter(c => c.ativo).reduce((acc, c) => acc + c.peso, 0);
            const widthsProp: string[] = colWeightsProp
                .filter(c => c.ativo)
                .map(c => `${((c.peso / totalPesoProp) * 100).toFixed(2)}%`);

            content.push({
                table: {
                    widths: widthsProp,
                    body: bodyInsumos
                },
                layout: {
                    fillColor: function (rowIndex: number, node: any, columnIndex: number) {
                        if (rowIndex === 0) return '#a6a6a6'; // Header principal
                        return null;
                    }
                },
                margin: [0, 0, 0, 15]
            });
        });
    }

    return {
        info: {
            title: headerInfo?.nome_arquivo || headerInfo?.titulo || 'Relatório de insumos totalizados',
            author: headerInfo?.autor || '',
        },
        content: content,
        pageOrientation: 'landscape',
        footer: (currentPage: number, pageCount: number) => {
            const dataGeracao = dayjs().format("DD/MM/YYYY, HH:mm:ss");
            return {
                stack: [
                    { text: '', margin: [0, 5, 0, 0] },
                    {
                        canvas: [
                            {
                                type: 'rect',
                                x: 0,
                                y: 0,
                                w: 760,
                                h: 4,
                                linearGradient: ['#e5f1e1', '#5da57d', '#0e743f']
                            }
                        ],
                        margin: [0, 0, 0, 5]
                    },
                    { text: '', margin: [0, 5, 0, 0] },
                    {
                        columns: [
                            {
                                width: '*',
                                stack: [
                                    {
                                        image: 'imgLogoAlias',
                                        width: 50,
                                        margin: [0, 3, 0, 0],
                                    },
                                ],
                                alignment: 'left',
                            },
                            {
                                width: '*',
                                stack: [
                                    {
                                        text: `Gerado por: ${headerInfo?.autor}`,
                                        bold: true,
                                        alignment: 'center',
                                        fontSize: 7,
                                        color: '#4D4D4D'
                                    },
                                    {
                                        text: `${dataGeracao}`,
                                        alignment: 'center',
                                        fontSize: 7,
                                        color: '#4D4D4D'
                                    },
                                ],
                                margin: [0, 3, 0, 0],
                                alignment: 'center',
                            },
                            {
                                width: '*',
                                text: `${currentPage} de ${pageCount}`,
                                alignment: 'right',
                                fontSize: 8,
                                margin: [0, 3, 0, 0],
                                color: '#4D4D4D'
                            },
                        ],
                    },
                    {
                        width: '*',
                        text: headerInfo?.razaoSocial ?? '',
                        alignment: 'left',
                        fontSize: 6,
                        margin: [0, 3, 0, 10],
                        color: '#4D4D4D'
                    },
                ],
                margin: [40, 0, 40, 0],
            };
        },
        images: {
            imgLogoAlias: imgLogo
        },
        styles: {
            headerMain: {
                fontSize: 20,
                bold: true,
                color: 'black'
            },
            headerPropriedade: {
                fontSize: 14,
                bold: true
            },
            tableHeader: {
                bold: true,
                fontSize: 11,
                color: 'white',
                alignment: 'left'
            }
        },
        defaultStyle: {
            fontSize: 10,
            alignment: 'left'
        },
        pageMargins: [40, 40, 40, 60]
    };
};

export const gerarPdfDataUrl = async (
    dados: (typeEstruturaCompraInsumos | typeInsumos)[],
    headerInfo?: HeaderDef
): Promise<string> => {
    if (headerInfo?.logo && headerInfo.logo.startsWith("http")) {
        headerInfo.logo = await fetchImageAsBase64(headerInfo.logo);
    }

    return new Promise((resolve, reject) => {
        try {
            const docDefinition = gerarDocDefinition(dados, headerInfo);
            pdfMake.createPdf(docDefinition).getDataUrl((dataUrl) => {
                resolve(dataUrl);
            });
        } catch (err) {
            reject(err);
        }
    });
};

export const imprimirPdfCompraInsumos = async (
    dados: (typeEstruturaCompraInsumos | typeInsumos)[],
    headerInfo?: HeaderDef,
    opcoes?: OpcoesColunas
): Promise<void> => {
    if (headerInfo?.logo && headerInfo.logo.startsWith("http")) {
        headerInfo.logo = await fetchImageAsBase64(headerInfo.logo);
    }

    const docDefinition = gerarDocDefinition(dados, headerInfo, opcoes);

    return new Promise((resolve, reject) => {
        try {
            pdfMake.createPdf(docDefinition).getBlob((blob) => {
                const blobUrl = URL.createObjectURL(blob);

                // 1. Salva o título original da página
                const originalTitle = document.title;

                // 2. Define o título temporário (usado como nome do arquivo na janela de impressão/salvamento)
                const fileTitle = headerInfo?.nome_arquivo || headerInfo?.titulo || 'Relatório';
                const tempTitle = fileTitle.replace(/[/\\?%*:|"<>]/g, '-');
                document.title = tempTitle;

                // 3. Cria um iframe oculto na página
                const iframe = document.createElement('iframe');
                iframe.style.position = 'fixed';
                iframe.style.top = '0';
                iframe.style.left = '0';
                iframe.style.width = '1px';
                iframe.style.height = '1px';
                iframe.style.opacity = '0.01';
                iframe.style.pointerEvents = 'none';
                iframe.style.border = 'none';
                iframe.src = blobUrl;

                document.body.appendChild(iframe);

                let cleanedUp = false;
                const cleanUp = () => {
                    if (cleanedUp) return;
                    cleanedUp = true;

                    document.title = originalTitle;
                    URL.revokeObjectURL(blobUrl);
                    if (iframe.parentNode) {
                        document.body.removeChild(iframe);
                    }

                    // Remove ouvintes globais para evitar vazamento
                    window.removeEventListener('afterprint', cleanUp);
                    window.removeEventListener('focus', cleanUp);
                };

                // Ouvintes para restaurar o título original apenas quando o diálogo de impressão fechar
                window.addEventListener('afterprint', cleanUp);
                window.addEventListener('focus', cleanUp);

                iframe.onload = () => {
                    setTimeout(() => {
                        try {
                            iframe.contentWindow?.focus();
                            iframe.contentWindow?.print();

                            // Resolve a Promise imediatamente após disparar a janela de impressão
                            resolve();
                        } catch (printErr) {
                            console.error("Erro ao invocar a impressão do iframe:", printErr);
                            cleanUp();
                            reject(printErr);
                        }
                    }, 500); // Pequeno atraso para garantir o carregamento do PDF no iframe
                };

                iframe.onerror = (err) => {
                    cleanUp();
                    reject(err);
                };
            });
        } catch (err) {
            reject(err);
        }
    });
};
